import React, { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as L from "lucide-react";
import api from "@/lib/api";
import MemoryGarden from "@/components/MemoryGarden";
import { KnowledgeScreen } from "@/pages/app/screens/knowledge";

const CATEGORIES = [
  "Personal", "Family", "Work", "Goals", "Preferences",
  "Health", "Entertainment", "Important Dates",
  "Conversations", "Follow-ups",
];

/**
 * Memory screen wrapper — two tabs:
 *   • Memories — user-authored garden (planted facts + AI-extracted notes)
 *   • Knowledge — Rammy's knowledge graph (entities from every source)
 *
 * We wrap the existing MemoriesGardenScreen (unchanged internals) and
 * mount the fresh KnowledgeScreen next to it. The tab bar hangs at the
 * top of the container so it's stable across scroll.
 */
export function MemoriesScreen() {
  const [tab, setTab] = useState("memories");
  return (
    <div className="space-y-0" data-testid="screen-memory-wrapper">
      <div className="border-b border-[var(--rammy-border)] px-5 md:px-8 pt-5">
        <div className="max-w-5xl mx-auto flex items-center gap-1">
          <TabBtn active={tab === "memories"} onClick={() => setTab("memories")} testid="tab-memories">
            <L.Sparkles size={13}/> Memories
          </TabBtn>
          <TabBtn active={tab === "knowledge"} onClick={() => setTab("knowledge")} testid="tab-knowledge">
            <L.Network size={13}/> Knowledge
          </TabBtn>
        </div>
      </div>
      {tab === "memories" ? <MemoriesGardenScreen/> : <KnowledgeScreen/>}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
        active
          ? "border-[var(--rammy-cyan)] text-[var(--rammy-cyan-dark)]"
          : "border-transparent text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)]"
      }`}
    >
      {children}
    </button>
  );
}

function MemoriesGardenScreen() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [category, setCategory] = useState("Personal");
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/memories").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim()) return;
    await api.post("/memories", { title, detail, category });
    setTitle(""); setDetail(""); setCategory("Personal"); setShowAdd(false);
    await load();
    // Warm confirmation — this is the emotional payoff.
    toast.success("🌱 Planted in your garden.", {
      description: `I'll remember: ${title}`,
      duration: 3500,
    });
  };

  const del = async (id) => {
    await api.delete(`/memories/${id}`);
    load();
    toast("Forgot that one.");
  };

  const filtered = useMemo(() => items.filter(i =>
    !q || `${i.title} ${i.detail} ${i.category}`.toLowerCase().includes(q.toLowerCase())
  ), [items, q]);

  const onSelectDot = (m) => {
    setSelected(m.id);
    // scroll into view
    setTimeout(() => {
      document.getElementById(`mem-row-${m.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 30);
  };

  return (
    <div className="p-5 md:p-8 space-y-6 max-w-5xl mx-auto" data-testid="screen-memories">
      {/* The garden — always visible, even when empty. */}
      <MemoryGarden items={items} onSelect={onSelectDot} />

      {/* Search + add controls */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <L.Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rammy-text-muted)]" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search memories"
            data-testid="mem-search"
            className="pl-9 rounded-2xl h-10"
          />
        </div>
        <Button
          onClick={() => setShowAdd(s => !s)}
          className="btn-pill btn-dark h-10 px-4"
          data-testid="mem-add-toggle"
        >
          <L.Plus size={16} /> <span className="ml-1.5 hidden sm:inline">Add memory</span>
        </Button>
      </div>

      {showAdd && (
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4 space-y-3" data-testid="mem-add-form">
          <Input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} className="rounded-xl" data-testid="mem-input-title" />
          <Textarea placeholder="Detail (optional)" value={detail} onChange={e => setDetail(e.target.value)} rows={2} className="rounded-xl" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="rounded-xl" data-testid="mem-input-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={add} className="btn-pill btn-primary w-full" data-testid="mem-save">Plant this memory</Button>
        </div>
      )}

      {/* List — small stat above */}
      {items.length > 0 && (
        <div className="text-xs text-[var(--rammy-text-muted)] flex items-center gap-2">
          <L.Sparkles size={12} className="text-[var(--rammy-cyan-dark)]" />
          {items.length} memor{items.length === 1 ? "y" : "ies"}
          {q && ` · ${filtered.length} match${filtered.length === 1 ? "" : "es"} for "${q}"`}
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-sm text-[var(--rammy-text-muted)] py-8 text-center">
            No memories yet — chat with me and I&rsquo;ll start noticing things.
          </div>
        )}
        {filtered.map(m => (
          <div
            key={m.id}
            id={`mem-row-${m.id}`}
            data-testid={`mem-row-${m.id}`}
            className={`rounded-2xl border p-4 transition ${
              selected === m.id
                ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/8 rammy-shadow"
                : "border-[var(--rammy-border)] bg-[var(--rm-surface)]"
            }`}
          >
            <div className="flex items-start gap-3">
              {m.has_image && m.image_thumb && (
                <img
                  src={`data:${m.image_mime || "image/jpeg"};base64,${m.image_thumb}`}
                  alt=""
                  className="w-16 h-16 rounded-xl object-cover border border-[var(--rammy-border)] shrink-0"
                  data-testid={`mem-thumb-${m.id}`}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">{m.category}</div>
                <div className="font-medium mt-0.5">{m.title}</div>
                {m.detail && <div className="text-sm text-[var(--rammy-text-sec)] mt-1 leading-relaxed">{m.detail}</div>}
              </div>
              <button
                onClick={() => del(m.id)}
                className="text-[var(--rammy-text-muted)] hover:text-red-500"
                data-testid={`mem-del-${m.id}`}
                aria-label="Delete memory"
              >
                <L.Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
